import anime from 'animejs/lib/anime.es';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrderAPI } from '../../apis/order-api';
import { useSpotAPI } from '../../apis/spot-api';
import { Body } from '../../components/menu/body';
import { Footer } from '../../components/menu/footer';
import { Header } from '../../components/menu/header';
import { OAuthGoogleButton } from '../../components/auth/oauth-google-button';
import { useInterval } from '../../hooks/use-interval';
import { usePageview } from '../../hooks/use-pageview';
import { useSession } from '../../hooks/use-session';
import { useStorage } from '../../hooks/use-storage';
import { layoutDefaults } from '../../utils/layout-defaults';
import { getCache, setCache } from '../../utils/manager/cache-manager';
import { parseContainerBackgroundImage } from '../../utils/manager/layout-manager';
import { filterMenu } from '../../utils/manager/menu-filter';
import { useMenuManager } from '../../utils/manager/menu-manager';
import { useWelcomeManager } from '../../utils/manager/welcome-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';
import { wait } from '../../utils/wait';
import { useErrorModal } from '../error-modal';
import { useSpotHasChangedModal } from './spot-has-changed-modal';
import { useWelcomeModal } from './welcome-modal';

function MenuScreen() {
  const [updating, setUpdating] = useState(true);
  const { menuSlug, toBeIgnored } = useParams();
  const { analyticsPageView } = useAnalytics();
  const {
    menuCategories,
    menuLayout,
    spotRef,
    setSpot,
    loadedSpot,
    setLoadedSpot,
    showingModal,
    liveEditMode,
    setOrders,
    config,
    filter,
    filterCount,
    setFilterLoading,
    setFilterCount,
    setPaying,
  } = useStorage();
  const { prepareWelcomeModal, showWelcomeModal } = useWelcomeModal();
  const { findSpotOrdersFromRemote } = useOrderAPI();
  const showSpotHasChangedModal = useSpotHasChangedModal();
  const classes = useStyles({ layout: menuLayout, showingModal });
  const { loadMenu } = useMenuManager();
  const { spotCheckInFromRemote } = useSpotAPI();
  const { showErrorModal } = useErrorModal();
  const navigate = useNavigate();
  const [filtered, setFiltered] = useState();
  const containerRef = useRef();
  const { welcomeKey } = useWelcomeManager();
  useSession();

  usePageview('Menu');
  useEffect(() => {
    setPaying(false);
  }, []);

  useLayoutEffect(() => {
    if (toBeIgnored != null) {
      navigate(`/${menuSlug}`, { replace: true });
    }
  }, [toBeIgnored]);

  useLayoutEffect(() => {
    if (menuCategories != null && filter != null) {
      loadFiltered();
    }
  }, [menuCategories, filter]);

  useInterval(
    () => {
      console.log('updateMenu');

      loadMenu({
        includesOrders: true,
        includesFonts: true,
      });
    },
    liveEditMode ? 2000 : 20000,
    liveEditMode || updating,
    [liveEditMode]
  );

  async function checkIn() {
    let result = null;

    if (menuSlug != null && loadedSpot != null) {
      const newSpot = loadedSpot;
      const oldSpot = spotRef.current;

      try {
        if (newSpot != null && oldSpot?.id !== newSpot.id) {
          result = newSpot;

          setSpot(newSpot);
          await spotCheckInFromRemote(newSpot.id);

          const response = await findSpotOrdersFromRemote();
          setOrders(response.data);
          setLoadedSpot(null);

          await showSpotHasChangedModal({
            orders: response.data,
            oldSpot,
            newSpot,
          });
          analyticsPageView('Menu');
          // }
        }
      } catch (error) {
        if (error.response?.status === 422) {
          setLoadedSpot(null);
          setSpot(oldSpot);

          await loadMenu();
          await showErrorModal(error);
        } else if (error.response?.status !== 404) {
          console.error(error);
          // TODO enviar erro para o GA
        }
      }
    }

    return result;
  }

  async function welcome() {
    const welcomed = getCache(menuSlug, welcomeKey);
    let newSpot = null;

    if (loadedSpot != null) {
      if (loadedSpot.hidden) {
        setSpot(null);
        setLoadedSpot(null);
      } else {
        newSpot = await checkIn();
      }
    }

    if (!welcomed && newSpot == null) {
      await prepareWelcomeModal();

      _.delay(async () => {
        const result = await showWelcomeModal();

        if (result != null) {
          setCache(menuSlug, welcomeKey, dayjs().toISOString(), {
            shelfLife: { value: 1, unit: 'M' },
          });
        }
      }, layoutDefaults.modalTimer);
    }
  }

  useLayoutEffect(() => {
    if (menuSlug != null) {
      welcome();
    }
  }, [menuSlug, loadedSpot]);

  useLayoutEffect(() => {
    anime({
      targets: containerRef.current,
      opacity: showingModal ? 0.2 : 1,
      duration: showingModal ? 400 : 200,
      easing: 'easeInQuad',
    });
  }, [showingModal]);

  useLayoutEffect(() => {
    loadFiltered();
  }, [filter]);

  async function loadFiltered() {
    // if (filterLoadingRef.current) return;
    setFilterLoading(true);

    try {
      if (!_.isEmpty(filter)) {
        await wait(1200);
      }

      const [lFiltered, lFilterCount] = filterMenu(menuCategories, filter);
      const count = lFilterCount.items;

      if (!_.isEqual(filtered, lFiltered)) {
        setFiltered(lFiltered);
      }

      if (filterCount !== count) {
        setFilterCount(count);
      }
    } catch (error) {
      console.error(error);
    }

    setFilterLoading(false);
  }

  return (
    <div ref={containerRef} className={classes.container}>
      <Header
      // onFilterChange={onFilterChange}
      />
      <Body categories={filtered || menuCategories} setUpdating={setUpdating} />
      <Footer />
      <OAuthGoogleButton config={config} visible={false} />
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    minHeight: '100vh',
    overflow: 'clip',
    fontSize: layoutDefaults.fontSize,
    fontFamily: ({ layout }) =>
      layout?.text?.default_font ?? layoutDefaults.fontFamily,
    backgroundImage: ({ layout }) => parseContainerBackgroundImage(layout),
  },
});

export { MenuScreen };
